import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import pmvService from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';
import { humanizeAttentionPhrase } from '@/utils/pmvUxCopy';

const { Text, Title } = Typography;

type FleetRow = {
  profileId: string;
  assetCode: string | null;
  assetName: string | null;
  allocationId: string;
  allocationStatus: string;
  logStatus: string | null;
  logState: string;
  workingMinutes: number;
  idleMinutes: number;
  standbyMinutes: number;
  breakdownMinutes: number;
  workingUtilization: number | null;
  attention: string[];
  logId: string | null;
};

type LaneKey =
  | 'requests'
  | 'reservations'
  | 'dispatch'
  | 'transit'
  | 'deployed'
  | 'attention'
  | 'release';

const LANE_META: Record<LaneKey, { title: string; hint: string }> = {
  requests: { title: 'Requests', hint: 'Open / submitted demand' },
  reservations: { title: 'Reserved', hint: 'Holds awaiting allocation' },
  dispatch: { title: 'Dispatch ready', hint: 'Allocated — prepare gate out' },
  transit: { title: 'In transit', hint: 'Dispatched to site' },
  deployed: { title: 'Deployed', hint: 'On site today' },
  attention: { title: 'Attention', hint: 'Missing logs / blockers' },
  release: { title: 'Due for release', hint: 'Near planned end' },
};

function laneForAllocationStatus(status: string): LaneKey | null {
  const s = status.toUpperCase();
  if (s.includes('RESERV')) return 'reservations';
  if (s === 'DISPATCH_READY' || s === 'ALLOCATED' || s === 'PLANNED') return 'dispatch';
  if (s === 'IN_TRANSIT' || s === 'DISPATCHED') return 'transit';
  if (s === 'ON_SITE' || s === 'DEPLOYED' || s === 'ACTIVE' || s === 'RECEIVED') return 'deployed';
  if (s.includes('RELEASE') || s.includes('DEMOB')) return 'release';
  return 'deployed';
}

const PmvOperationsBoardPage: React.FC = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);
  const [drawerRow, setDrawerRow] = useState<FleetRow | null>(null);

  const summaryQ = useQuery({
    queryKey: ['pmv-operations-summary'],
    queryFn: () => pmvService.getOperationsSummary(),
  });

  const dailyQ = useQuery({
    queryKey: ['pmv-operations-daily', today],
    queryFn: () => pmvService.getOperationsDailySummary(today),
  });

  const requestsQ = useQuery({
    queryKey: ['pmv-requests', 'deployment-board'],
    queryFn: () => pmvService.listRequests({ pageSize: 50, status: 'SUBMITTED' }),
  });

  const s = summaryQ.data ?? {};
  const d = dailyQ.data;
  const fleet = (d?.fleet ?? []) as FleetRow[];
  const requests = requestsQ.data?.data ?? [];

  const lanes = useMemo(() => {
    const map: Record<LaneKey, FleetRow[]> = {
      requests: [],
      reservations: [],
      dispatch: [],
      transit: [],
      deployed: [],
      attention: [],
      release: [],
    };
    for (const row of fleet) {
      if (row.logState === 'MISSING' || !row.logId || (row.attention?.length ?? 0) > 0) {
        map.attention.push(row);
      }
      const lane = laneForAllocationStatus(row.allocationStatus);
      if (lane) map[lane].push(row);
    }
    return map;
  }, [fleet]);

  const lifecycleCurrent = (() => {
    if ((s.inTransit ?? 0) > 0) return 5;
    if ((s.deployed ?? 0) > 0) return 6;
    if ((s.reservations ?? 0) > 0) return 2;
    if ((s.submittedRequests ?? 0) > 0) return 1;
    return 0;
  })();

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Deployment Control"
          documentNumber={today}
          statusTag={<Tag color="blue">Operations</Tag>}
          subtitle={<Text type="secondary">Request → Reserve → Allocate → Dispatch → Receive → Deploy → Release</Text>}
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/control-tower')}>Command Center</Button>
              <Button type="primary" onClick={() => navigate('/asset/pmv/requests/new')}>
                New request
              </Button>
            </Space>
          }
        />
      }
      workflow={
        <WorkflowStrip
          compact
          current={lifecycleCurrent}
          stages={[
            { title: 'Requested' },
            { title: 'Matched' },
            { title: 'Reserved' },
            { title: 'Allocated' },
            { title: 'Dispatch' },
            { title: 'In transit' },
            { title: 'Deployed' },
            { title: 'Released' },
          ]}
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={[12, 12]}>
          <Col xs={12} sm={8} md={4}>
            <ControlTowerMetricTile
              title="Open requests"
              count={s.submittedRequests ?? 0}
              urgency={(s.submittedRequests ?? 0) > 0 ? 'attention' : 'neutral'}
              footerLink={{ label: 'Requests', onClick: () => navigate('/asset/pmv/requests') }}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <ControlTowerMetricTile title="Reservations" count={s.reservations ?? 0} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <ControlTowerMetricTile
              title="In transit"
              count={s.inTransit ?? 0}
              urgency={(s.inTransit ?? 0) > 0 ? 'attention' : 'neutral'}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <ControlTowerMetricTile title="Deployed" count={s.deployed ?? 0} />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <ControlTowerMetricTile
              title="Missing logs"
              count={d?.missingLogs ?? 0}
              urgency={(d?.missingLogs ?? 0) > 0 ? 'critical' : 'neutral'}
              footerLink={{ label: 'Field ops', onClick: () => navigate('/asset/pmv/daily-logs') }}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <ControlTowerMetricTile
              title="Utilization"
              count={0}
              valueDisplay={
                d?.averageWorkingUtilization != null ? `${d.averageWorkingUtilization}%` : 'Incomplete'
              }
              urgency={d?.averageWorkingUtilization == null ? 'attention' : 'neutral'}
            />
          </Col>
        </Row>

        {(d?.attention?.length ?? 0) > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Deployment attention"
            description={d!.attention.map(humanizeAttentionPhrase).join(' · ')}
          />
        )}

        <div>
          <Title level={5} style={{ marginTop: 0 }}>
            Operational lanes
          </Title>
          <Text type="secondary">Select a card for context and the next domain action. No drag-and-drop.</Text>
        </div>

        <Row gutter={[12, 12]}>
          {(Object.keys(LANE_META) as LaneKey[]).map((key) => {
            if (key === 'requests') {
              return (
                <Col xs={24} md={12} xl={8} key={key}>
                  <Card
                    size="small"
                    title={
                      <Space>
                        {LANE_META[key].title}
                        <Badge count={requests.length} showZero color="#1677ff" />
                      </Space>
                    }
                    extra={<Text type="secondary">{LANE_META[key].hint}</Text>}
                    styles={{ body: { maxHeight: 320, overflow: 'auto' } }}
                  >
                    {requests.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No submitted requests" />
                    ) : (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {requests.map((r) => (
                          <Card
                            key={r.id}
                            size="small"
                            hoverable
                            onClick={() => navigate(`/asset/pmv/requests/${r.id}`)}
                          >
                            <Space direction="vertical" size={0}>
                              <Text strong>{r.request_number}</Text>
                              <Text type="secondary">
                                {resolveProjectDisplayFromMap(projectLabels, r.project_id)}
                              </Text>
                              <Tag>{r.status}</Tag>
                            </Space>
                          </Card>
                        ))}
                      </Space>
                    )}
                  </Card>
                </Col>
              );
            }

            const rows = lanes[key];
            return (
              <Col xs={24} md={12} xl={8} key={key}>
                <Card
                  size="small"
                  title={
                    <Space>
                      {LANE_META[key].title}
                      <Badge
                        count={rows.length}
                        showZero
                        color={key === 'attention' ? '#cf1322' : '#1677ff'}
                      />
                    </Space>
                  }
                  extra={<Text type="secondary">{LANE_META[key].hint}</Text>}
                  styles={{ body: { maxHeight: 320, overflow: 'auto' } }}
                >
                  {rows.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="None" />
                  ) : (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {rows.map((row) => (
                        <Card
                          key={`${key}-${row.allocationId}`}
                          size="small"
                          hoverable
                          onClick={() => setDrawerRow(row)}
                        >
                          <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Text strong>{row.assetCode || row.assetName || 'Equipment'}</Text>
                            <Text type="secondary" ellipsis>
                              {row.assetName && row.assetCode ? row.assetName : null}
                            </Text>
                            <Space wrap>
                              <Tag>{row.allocationStatus.replace(/_/g, ' ')}</Tag>
                              {!row.logId ? (
                                <Tag color="error">Daily log missing</Tag>
                              ) : (
                                <Tag color="processing">{row.logStatus}</Tag>
                              )}
                            </Space>
                          </Space>
                        </Card>
                      ))}
                    </Space>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>

        <Card size="small" title="Today — hours">
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Statistic title="Working" value={d?.workingHours ?? 0} precision={1} suffix="h" />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Logs certified" value={d?.logsCertified ?? 0} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Logs submitted" value={d?.logsSubmitted ?? 0} />
            </Col>
            <Col xs={12} md={6}>
              <Button block onClick={() => navigate('/asset/pmv/daily-logs')}>
                Open field operations
              </Button>
            </Col>
          </Row>
        </Card>
      </Space>

      <Drawer
        title="Deployment context"
        open={Boolean(drawerRow)}
        onClose={() => setDrawerRow(null)}
        width={Math.min(420, typeof window !== 'undefined' ? window.innerWidth - 24 : 420)}
      >
        {drawerRow && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={5} style={{ margin: 0 }}>
                {drawerRow.assetCode || 'Unit'}
              </Title>
              <Text type="secondary">{drawerRow.assetName}</Text>
            </div>
            <Space wrap>
              <Tag>{drawerRow.allocationStatus.replace(/_/g, ' ')}</Tag>
              {!drawerRow.logId ? (
                <Tag color="error">Daily log missing</Tag>
              ) : (
                <Tag>{drawerRow.logStatus}</Tag>
              )}
            </Space>
            <Text>
              Working {(drawerRow.workingMinutes / 60).toFixed(1)} h · Idle{' '}
              {(drawerRow.idleMinutes / 60).toFixed(1)} h · Standby{' '}
              {(drawerRow.standbyMinutes / 60).toFixed(1)} h
            </Text>
            <Text>
              Utilization:{' '}
              {drawerRow.workingUtilization != null
                ? `${drawerRow.workingUtilization}%`
                : 'Unavailable — certified log required'}
            </Text>
            {(drawerRow.attention?.length ?? 0) > 0 && (
              <Alert
                type="warning"
                showIcon
                message={drawerRow.attention.map(humanizeAttentionPhrase).join(' · ')}
              />
            )}
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="primary" block onClick={() => navigate(`/asset/pmv/allocations/${drawerRow.allocationId}`)}>
                Open allocation
              </Button>
              <Button block onClick={() => navigate(`/asset/pmv/${drawerRow.profileId}`)}>
                Equipment passport
              </Button>
              {drawerRow.logId ? (
                <Button block onClick={() => navigate(`/asset/pmv/daily-logs/${drawerRow.logId}`)}>
                  Open daily log
                </Button>
              ) : (
                <Button
                  block
                  type="default"
                  onClick={() => navigate(`/asset/pmv/allocations/${drawerRow.allocationId}`)}
                >
                  Create daily log from allocation
                </Button>
              )}
            </Space>
          </Space>
        )}
      </Drawer>
    </WorkspaceLayout>
  );
};

export default PmvOperationsBoardPage;
